/**
 * Shared UI components for guardian/principal forms.
 * Used across Bond Retrieval, Bond Creation Historic, and Features/Bonds components.
 */
import React from "react";
import PropTypes from "prop-types";
import {
  TextField,
  MenuItem,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
} from "@mui/material";

/**
 * Two Yes/No dropdowns for selecting Professional Guardian and Attorney modes.
 */
export const PrincipalTypeSelectors = ({
  guardian,
  isDisabled,
  isGuest,
  showPgSelector = true,
  showAttorneySelector = true,
  onProfessionalGuardianChange,
  onAttorneyChange,
}) => {
  const isProfessionalGuardian = guardian?.is_professional_guardian === "Yes";

  return (
    <>
      {showPgSelector && !isGuest && (
        <TextField
          select
          label="Is Principal a Professional Guardian?"
          value={guardian?.is_professional_guardian || "No"}
          disabled={isDisabled}
          onChange={(e) => onProfessionalGuardianChange(e.target.value)}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      )}

      {showAttorneySelector && !isGuest && !isProfessionalGuardian && (
        <TextField
          select
          label="Is Principal an Attorney?"
          value={guardian?.is_attorney || "No"}
          disabled={isDisabled}
          onChange={(e) => onAttorneyChange(e.target.value)}
          fullWidth
          required
        >
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </TextField>
      )}
    </>
  );
};

PrincipalTypeSelectors.propTypes = {
  guardian: PropTypes.object,
  isDisabled: PropTypes.bool,
  isGuest: PropTypes.bool,
  showPgSelector: PropTypes.bool,
  showAttorneySelector: PropTypes.bool,
  onProfessionalGuardianChange: PropTypes.func.isRequired,
  onAttorneyChange: PropTypes.func.isRequired,
};

/**
 * Styled card showing selected entity (PG or Attorney) details.
 */
export const SelectedEntityCard = ({ title, rows }) => {
  if (!rows || rows.length === 0) return null;

  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        border: "1px solid #e0e0e0",
        borderRadius: 2,
        backgroundColor: "#f9f9f9",
        boxShadow: "0px 2px 5px rgba(0,0,0,0.1)",
      }}
    >
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        {title}
      </Typography>
      {rows.map((row) => (
        <Typography key={`${title}-${row.label}`} variant="body1">
          <strong>{row.label}:</strong> {row.value || "—"}
        </Typography>
      ))}
    </Box>
  );
};

SelectedEntityCard.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
};

/**
 * Professional Guardian autocomplete picker with "not in list" link.
 */
export const ProfessionalGuardianPicker = ({
  options,
  value,
  loading,
  isDisabled,
  onSelect,
  /** When user clears selection (Autocomplete clear). Omit if selection cannot be cleared. */
  onClear,
  onManualEntry,
  selectedPG,
  /** Merged onto root Box (overrides default top margin where needed). */
  containerSx,
}) => {
  const selectedOption = options.find((pg) => pg.value === value) || null;

  return (
    <Box sx={{ mt: 4, ...containerSx }}>
      <Autocomplete
        options={options}
        value={selectedOption}
        getOptionLabel={(option) => option.label || ""}
        loading={loading}
        disabled={isDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Professional Guardian"
            variant="outlined"
            fullWidth
            slotProps={{ input: { ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}}
          />
        )}
        onChange={(event, newValue) => {
          if (newValue) {
            onSelect(newValue.value);
          } else if (onClear) {
            onClear();
          }
        }}
      />

      {selectedPG && (
        <SelectedEntityCard
          title="Selected Professional Guardian:"
          rows={[
            { label: "Name", value: selectedPG.label },
            { label: "Address", value: selectedPG.address },
            { label: "Cell Phone", value: selectedPG.cell },
          ]}
        />
      )}

      <Typography
        variant="body2"
        className={`mt-2 ${isDisabled ? "text-gray-400 cursor-not-allowed" : "text-blue-500 cursor-pointer"}`}
        onClick={() => {
          if (!isDisabled) {
            onManualEntry();
          }
        }}
        sx={{ mt: 2 }}
      >
        Professional Guardian not in the list?
      </Typography>
    </Box>
  );
};

ProfessionalGuardianPicker.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onClear: PropTypes.func,
  onManualEntry: PropTypes.func.isRequired,
  selectedPG: PropTypes.shape({
    label: PropTypes.string,
    address: PropTypes.string,
    cell: PropTypes.string,
  }),
  containerSx: PropTypes.object,
};

/**
 * Attorney autocomplete picker with 3-char filter.
 */
export const AttorneyPicker = ({
  options,
  value,
  loading,
  isDisabled,
  onSelect,
  selectedAttorney,
}) => {
  const selectedOption = options.find((a) => a.value === value) || null;

  return (
    <Box sx={{ mt: 2 }}>
      <Autocomplete
        options={options}
        value={selectedOption}
        getOptionLabel={(option) => option.label || ""}
        filterOptions={(opts, { inputValue }) => {
          if (!inputValue || inputValue.length < 3) return [];
          const input = inputValue.toLowerCase();
          return opts.filter((opt) => (opt.label || "").toLowerCase().includes(input));
        }}
        noOptionsText="Type at least 3 characters to search for attorneys"
        loading={loading}
        disabled={isDisabled}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search Attorney"
            variant="outlined"
            fullWidth
            slotProps={{ input: { ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}}
          />
        )}
        onChange={(event, newValue) => {
          onSelect(newValue);
        }}
      />

      {selectedAttorney && (
        <SelectedEntityCard
          title="Selected Attorney Details:"
          rows={[
            { label: "Name", value: selectedAttorney.label },
            { label: "Position", value: selectedAttorney.position },
            { label: "Office Address", value: selectedAttorney.address },
            {
              label: "City/State/Zip",
              value: `${selectedAttorney.city || ""}, ${selectedAttorney.state || ""} ${selectedAttorney.zip_code || ""}`.trim(),
            },
          ]}
        />
      )}
    </Box>
  );
};

AttorneyPicker.propTypes = {
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  loading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  selectedAttorney: PropTypes.shape({
    label: PropTypes.string,
    position: PropTypes.string,
    address: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip_code: PropTypes.string,
  }),
};

/**
 * Home address selection for attorneys.
 */
export const AttorneyHomeAddressSelector = ({
  value,
  isDisabled,
  onChange,
}) => (
  <Box sx={{ mb: 3, mt: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
      Step 1: Address Information
    </Typography>
    <TextField
      select
      label="Use attorney's home address?"
      value={value || "No"}
      onChange={(e) => onChange(e.target.value)}
      fullWidth
      required
      disabled={isDisabled}
    >
      <MenuItem value="Yes">Yes</MenuItem>
      <MenuItem value="No">No</MenuItem>
    </TextField>
  </Box>
);

AttorneyHomeAddressSelector.propTypes = {
  value: PropTypes.string,
  isDisabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

/**
 * Employment status and occupation fields for high-bond principals.
 */
export const EmploymentOccupationFields = ({
  employmentStatus,
  occupationText,
  isDisabled,
  onEmploymentStatusChange,
  onOccupationTextChange,
  questionLabel = "What is your current occupation?",
}) => (
  <>
    <TextField
      select
      label="Employment Status"
      value={employmentStatus || ""}
      onChange={(e) => onEmploymentStatusChange(e.target.value)}
      fullWidth
      required
      disabled={isDisabled}
    >
      <MenuItem value="Employed">Employed</MenuItem>
      <MenuItem value="Unemployed">Unemployed</MenuItem>
      <MenuItem value="Retired">Retired</MenuItem>
    </TextField>

    {(employmentStatus === "Employed" || employmentStatus === "Retired") && (
      <TextField
        label={questionLabel}
        value={occupationText || ""}
        onChange={(e) => onOccupationTextChange(e.target.value)}
        fullWidth
        required
        disabled={isDisabled}
      />
    )}
  </>
);

EmploymentOccupationFields.propTypes = {
  employmentStatus: PropTypes.string,
  occupationText: PropTypes.string,
  isDisabled: PropTypes.bool,
  onEmploymentStatusChange: PropTypes.func.isRequired,
  onOccupationTextChange: PropTypes.func.isRequired,
  questionLabel: PropTypes.string,
};

/**
 * Loading spinner for async data.
 */
export const LoadingSpinner = ({ message = "Loading..." }) => (
  <Box display="flex" justifyContent="center" alignItems="center" my={3}>
    <CircularProgress />
    {message && (
      <Typography sx={{ ml: 2 }} variant="body2" color="text.secondary">
        {message}
      </Typography>
    )}
  </Box>
);

LoadingSpinner.propTypes = {
  message: PropTypes.string,
};
